import { FC, useEffect, useState } from 'react';
import { Alert, Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { api } from 'services/api';
import {
	getPgErrorCode,
	getPgErrorMessage,
	getPgErrorStatus,
} from 'services/api/paymentGateway/errors';
import { TPgPaymentDetails } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { useIdempotencyKey } from '../common/helpers';
import { formatMoney, parseDecimal } from '../utils';
import { chargedCurrencyOf, hasOpenDispute, refundableOf } from './utils';

type TRefundDialogProps = {
	open: boolean;
	payment: TPgPaymentDetails;
	onClose: () => void;
	/** The refund was accepted by the gateway (it may still be settled later). */
	onDone: () => void;
};

/**
 * Refund of a payment (03 §4.9, 04 §14): full remainder or a part, a mandatory reason and an optional
 * correction of the paid amount in EAS. A dispute or a late failure needs a second, separate
 * confirmation (409 `payment_disputed` → repeat with `allowDuringDispute`).
 */
export const RefundDialog: FC<TRefundDialogProps> = ({ open, payment, onClose, onDone }) => {
	const [idempotencyKey, renewKey] = useIdempotencyKey(open);
	const [amount, setAmount] = useState('');
	const [adjustment, setAdjustment] = useState('');
	const [notifyCustomer, setNotifyCustomer] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [disputeConfirm, setDisputeConfirm] = useState<{ reason: string } | null>(null);

	useEffect(() => {
		if (!open) return;
		setAmount('');
		setAdjustment('');
		setNotifyCustomer(true);
		setError(null);
		setDisputeConfirm(null);
		setBusy(false);
	}, [open]);

	const currency = chargedCurrencyOf(payment);
	const refundable = refundableOf(payment);
	const disputed = hasOpenDispute(payment);
	const isInvoice = payment.purpose === 'invoice';

	const amountValue = amount.trim() === '' ? null : parseDecimal(amount);
	const adjustmentValue = adjustment.trim() === '' ? null : parseDecimal(adjustment);
	const amountInvalid =
		amount.trim() !== '' && (amountValue === null || amountValue <= 0 || amountValue > refundable);
	const adjustmentInvalid =
		adjustment.trim() !== '' && (adjustmentValue === null || adjustmentValue < 0);

	const submit = async (reason: string, allowDuringDispute: boolean) => {
		setBusy(true);
		setError(null);
		try {
			const created = await api.paymentGateway.createRefund({
				transactionId: payment.transactionId,
				body: {
					amount: amountValue,
					reason,
					easAdjustmentEur: isInvoice ? adjustmentValue : null,
					notifyCustomer,
					idempotencyKey,
					allowDuringDispute: allowDuringDispute || undefined,
				},
			});
			if (created.status === 'failed') {
				// A final failure: a repeat with the same key would only return it again.
				renewKey();
				setError(created.failureMessage ?? 'Airwallex rejected the refund.');
				return;
			}
			notificationContainer(
				`Refund of ${formatMoney(created.amount, created.currency)} created (${created.status}).`,
				'success',
			);
			onDone();
		} catch (e) {
			if (
				getPgErrorStatus(e) === 409 &&
				getPgErrorCode(e) === 'payment_disputed' &&
				!allowDuringDispute
			) {
				setDisputeConfirm({ reason });
			} else {
				// `insufficient_balance`, `fx_conversion_in_progress`, `refund_in_progress`, 400: the gateway
				// explains what to do in `message`.
				setError(getPgErrorMessage(e, 'The refund failed'));
			}
		} finally {
			setBusy(false);
		}
	};

	return (
		<>
			<PgConfirmDialog
				open={open && !disputeConfirm}
				title="Refund payment"
				confirmLabel="Refund"
				confirmColor="error"
				reasonLabel="Reason (shown in the audit log)"
				confirmDisabled={amountInvalid || adjustmentInvalid || refundable <= 0}
				busy={busy}
				error={error}
				onConfirm={(reason) => void submit(reason, false)}
				onClose={onClose}
				description={
					<Alert severity="info" sx={{ mb: 2 }}>
						The refund is sent to Airwallex and returns the money to the customer&apos;s payment
						method. Refundable now: <b>{formatMoney(refundable, currency)}</b>.
					</Alert>
				}
			>
				{disputed && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						This payment has an open dispute or a late failure
						{payment.disputeStatus ? ` (${payment.disputeStatus})` : ''}. The money may already be
						returned to the customer: you will be asked to confirm the refund separately.
					</Alert>
				)}
				<TextField
					label={`Amount, ${currency} (empty: the whole remainder)`}
					value={amount}
					onChange={(e) => setAmount(e.target.value)}
					size="small"
					fullWidth
					error={amountInvalid}
					helperText={amountInvalid ? `From 0.01 to ${formatMoney(refundable)}` : undefined}
					disabled={busy}
					sx={{ mb: 2 }}
				/>
				{isInvoice && (
					<>
						<TextField
							label="Reduce the paid amount in EAS, EUR (empty: the refunded amount in EUR)"
							value={adjustment}
							onChange={(e) => setAdjustment(e.target.value)}
							size="small"
							fullWidth
							error={adjustmentInvalid}
							disabled={busy}
						/>
						<Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
							Warning: the EAS core sends the customer an &quot;Invoice payment received&quot;
							e-mail on any invoice update. To avoid it, enter 0 and correct the payment in Control
							Center manually.
						</Typography>
					</>
				)}
				<FormControlLabel
					control={
						<Checkbox
							checked={notifyCustomer}
							onChange={(e) => setNotifyCustomer(e.target.checked)}
							disabled={busy}
						/>
					}
					label="Notify the customer by e-mail"
				/>
			</PgConfirmDialog>
			<PgConfirmDialog
				open={open && !!disputeConfirm}
				title="Refund during a dispute?"
				confirmLabel="Refund anyway"
				confirmColor="error"
				busy={busy}
				error={error}
				onConfirm={() => disputeConfirm && void submit(disputeConfirm.reason, true)}
				onClose={() => setDisputeConfirm(null)}
				description={
					'This payment has an open dispute or a late failure. A Bacs indemnity claim or a card chargeback ' +
					'may already have returned the money to the customer: refunding as well pays it back twice ' +
					'and the second payment cannot be recovered. Refund anyway?'
				}
			/>
		</>
	);
};
